import { useRef } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';

import { type AccountsFilter } from '@pkg/graphql/codegen/graphql';

import { callGetAccounts } from '@app/store/zustand/wallet/request';

import { parseData } from './parseData';

interface UseAccountsQuery {
    limit?: number;
    pageOwnerId?: string | null;
    accountsFilter?: AccountsFilter;
}

export const USER_WALLETS_BY_USER_CACHE = 'user-accounts';

// `100` seems way too high for NFTs, which are our heaviest resources.
export function useAccountsItems({ limit = 100, pageOwnerId, accountsFilter }: UseAccountsQuery) {
    const isFailing = useRef(false);

    const baseQueryKey = [USER_WALLETS_BY_USER_CACHE, pageOwnerId, limit];
    const queryKey = accountsFilter ? [...baseQueryKey, ...Object.values(accountsFilter)] : baseQueryKey;

    const disabled = !pageOwnerId || isFailing.current;

    const { data, ...rest } = useInfiniteQuery({
        queryKey: queryKey,
        queryFn: ({ signal, pageParam }) =>
            callGetAccounts({
                variables: {
                    userFilter: pageOwnerId,
                    accountsFilter,
                    withCollectibles: true,
                    cursorInput: {
                        limit,
                        cursor: pageParam || null,
                    },
                },
                settings: { signal },
            }),
        initialPageParam: '',
        select: (response) => parseData(response),
        getNextPageParam: (response) => response?.data?.accounts?.cursor || null,
        staleTime: Infinity,
        enabled: !disabled,
        retry: false,
    });

    isFailing.current = Boolean(rest?.isError || (!rest.isPending && !data));

    return { data, ...rest };
}
